import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@components/common/PageHeader';
import Select from '@components/Form/Select/Select';
import cohortsService from '@/services/cohorts/cohortsService';
import EvaluationGrid from './EvaluationGrid';

const EvaluationsPage = () => {
    const { t } = useTranslation('evaluation');
    const [searchParams, setSearchParams] = useSearchParams();

    const [cohortId, setCohortId] = useState(searchParams.get('cohort') ?? '');

    // Keep the chosen cohort in the URL so the page is shareable / survives refresh.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (cohortId) next.set('cohort', cohortId);
        else next.delete('cohort');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohortId]);

    const { data: cohortsRes } = useQuery({
        queryKey: ['evaluation-cohorts'],
        queryFn: () => cohortsService.getCohorts(1, 200),
        staleTime: 5 * 60 * 1000,
    });

    const cohorts = cohortsRes?.data?.data ?? [];
    const cohortOptions = useMemo(
        () =>
            cohorts.map((c) => ({
                value: c.id,
                label: `${c.generated_cohort_title}${c.status !== 'ACTIVE' ? ` (${c.status.toLowerCase()})` : ''}`,
            })),
        [cohorts],
    );
    const cohortName = cohorts.find((c) => c.id === cohortId)?.generated_cohort_title;

    return (
        <>
            <PageHeader
                title={t('title')}
                breadcrumbItems={[
                    { label: t('breadcrumb'), to: '/admin/dashboard' },
                    { label: t('title') },
                ]}
            />

            <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
                <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] p-3 2xl:p-5">
                    <p className="text-xs text-[var(--subtitle-color)] mb-3">{t('subtitle')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 lg:gap-4 2xl:gap-5">
                        <Select
                            label={t('filters.cohort')}
                            placeholder={t('filters.cohortPlaceholder')}
                            options={cohortOptions}
                            value={cohortId}
                            onChange={(v) => setCohortId(v || '')}
                            isClearable={false}
                        />
                    </div>
                </div>

                {cohortId ? (
                    <EvaluationGrid cohortId={cohortId} cohortName={cohortName} />
                ) : (
                    <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] py-16 text-center text-sm text-[var(--subtitle-color)]">
                        {t('grid.noCohort')}
                    </div>
                )}
            </div>
        </>
    );
};

export default EvaluationsPage;
